import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import {
    IChecklistReportBusinessUnitData,
    IChecklistReportData,
    IChecklistReportUnitCheckPointDetail,
} from '../../checklist-report';
import { ChecklistModel } from '@fi-app/checklist/checklist.model';
import { ChecklistReportHelperService } from '../../checklist-report-helper.service';
import { AppTextService } from '@fi-shared/app-text';
import { ITableDataItem, ITableSchema } from '../checkpoints-per-unit-table/checkpoints-per-unit-table.component';
import { ObjectTypesService } from '@fi-app/utilities/fi-services/object-types.service';
import { ChecklistService } from '@fi-app/checklist/checklist.service';
import { PublicationType } from '@fi-shared/types/pubication-type';
import { ObjectTypes } from '@fi-shared/types/object-type';
import { createMap } from '@fi-shared/utils/create-map';
import { ChartOptions } from 'chart.js';
import { FiFormatDateService } from '@fi-shared/fi-format-date/fi-format-date.service';

@Component({
    selector: 'fi-checklist-report-checkpoints-per-unit',
    templateUrl: './checklist-report-checkpoints-per-unit.component.html',
    styleUrls: ['./checklist-report-checkpoints-per-unit.component.scss'],
    standalone: false,
})
export class ChecklistReportCheckpointsPerUnitComponent implements OnInit, OnChanges {
    @Input() reportData: IChecklistReportData;
    @Input() businessUnitsData: IChecklistReportBusinessUnitData[];
    @Input() checklist: ChecklistModel;
    @Input() status: any;

    chartData: number[];
    chartLabels: string[];
    chartColors: string[];
    UnitCheckPointDetails: IChecklistReportUnitCheckPointDetail[];

    chartOptions: ChartOptions<'bar'>;
    sortOrder: { prop: string; reverse: boolean };

    tableData: ITableDataItem[];
    tableSchema: ITableSchema;

    constructor(
        private checklistReportHelperService: ChecklistReportHelperService,
        private appTextService: AppTextService,
        private objectTypesService: ObjectTypesService,
        private checklistService: ChecklistService,
        private fiFormatDateService: FiFormatDateService,
    ) {}

    ngOnInit() {
        this.chartOptions = {
            plugins: {
                legend: {
                    display: false,
                },
            },
            scales: {
                y: {
                    ticks: {
                        callback: (value: number) => {
                            return Math.abs(Math.floor(value) - value) < 0.001 ? Math.floor(value) : '';
                        },
                    },
                    beginAtZero: true, // minimum value will be 0.
                },
            },
        };

        this.updateByStatus();
    }

    ngOnChanges(changes: SimpleChanges): void {
        let change = changes.status;
        if (change && !change.firstChange) {
            this.updateByStatus();
        }
        change = changes.reportData;
        if (change && !change.firstChange) {
            this.updateByStatus();
        }
    }

    updateByStatus() {
        this.sortOrder = {
            prop: 'Name',
            reverse: false,
        };
        this.updateChartData();
        this.updateTableData();
    }

    updateChartData() {
        this.UnitCheckPointDetails = this.reportData.Statuses.filter((item) => {
            return item.Status.toString() === this.getSelectedStatus();
        })[0].UnitCheckPointDetails;

        this.chartData = [];
        this.chartLabels = [];
        this.chartColors = [];

        this.UnitCheckPointDetails.forEach((item) => {
            this.chartData.push(item.AnswersCount);
            this.chartLabels.push(item.UnitName);
            this.chartColors.push(ChecklistModel.StatusColors[this.getSelectedStatus()]);
        });
    }

    updateTableData() {
        this.tableData = [];
        this.tableSchema = {
            columns: [
                {
                    prop: 'Name',
                    title: this.appTextService.get('common.unit'),
                    type: this.isNewUnitsSelected() ? 'textPlain' : 'text',
                    sortable: true,
                },
            ],
        };

        if (this.isNewUnitsSelected()) {
            this.tableSchema.columns.push({
                prop: 'PeriodText',
                arrayProp: 'NotFilledOutPeriods',
                title: this.appTextService.get('checklist.periods', {
                    '{status}': this.getStatusName(this.getSelectedStatus()),
                }),
                type: 'array',
                tooltipText: 'checklist.report_per_units_new_units_tooltip',
            });
        } else {
            // default title
            let title = this.appTextService.get('checklist.number_of_status_checkpoints', {
                '{status}': this.getStatusName(this.getSelectedStatus()),
            });
            // title per status
            if (this.isCompletedSelected()) {
                title = this.appTextService.get('checklist.checkpoints');
            } else if (this.isDeviationSelected()) {
                title = this.appTextService.get('checklist.deviations');
            } else if (this.isFailedSelected()) {
                title = this.appTextService.get('checklist_reports.failed_checkpoints');
            } else if (this.isOkCheckpointsSelected()) {
                title = this.appTextService.get('checklist.ok_checkpoints');
            } else if (this.isDeviationCheckpointsSelected()) {
                title = this.appTextService.get('checklist.deviations');
            }

            this.tableSchema.columns.push({
                prop: 'AnswersCount',
                title: title,
                type: 'text',
                sortable: true,
            });
        }

        if (this.isDeviationSelected() || this.isDeviationCheckpointsSelected()) {
            this.tableSchema.columns.push({
                sortProp: 'CheckPointName',
                prop: 'CheckPointName',
                arrayProp: 'CheckPoints',
                title: this.appTextService.get('checklist.checkpoints'),
                type: 'array',
            });
            this.tableSchema.columns.push({
                sortProp: 'CheckPointName',
                prop: 'RepeatPeriodsCount',
                arrayProp: 'CheckPoints',
                title: this.appTextService.get('checklist.periods'),
                type: 'array',
                tooltipText: 'checklist.report_checklist.checkpoints_units_deviation_tooltip',
            });
        }

        const unitsMap = createMap(this.UnitCheckPointDetails, 'UnitId'),
            addedItems = {};

        this.businessUnitsData.forEach((item) => {
            const nameForSorting = item.ObjectType + '-' + item.Name;

            item.ChildItems.forEach((unit) => {
                if (unitsMap.hasOwnProperty(unit.Id)) {
                    const key = item.ObjectType + '-' + item.Id;
                    if (!addedItems.hasOwnProperty(key)) {
                        const newItem = {
                            ObjectType: item.ObjectType,
                            Id: item.Id,
                            Name: item.Name,
                            NameForSorting: nameForSorting,
                            AnswersCount: 0,
                            Heading: true,
                            ChildItems: [],
                        };

                        this.tableData.push(newItem);

                        if (item.ObjectType === ObjectTypes.Cluster) {
                            newItem.Name += ' (' + this.appTextService.get('common.cluster') + ')';
                        }

                        addedItems[key] = newItem;
                    }

                    addedItems[key].ChildItems.push({
                        ObjectType: unit.ObjectType,
                        Id: unit.Id,
                        Name: unit.Name,
                        NameForSorting: nameForSorting,
                        AnswersCount: unitsMap[unit.Id].AnswersCount,
                        NotFilledOutPeriods: unitsMap[unit.Id].NotFilledOutPeriods.map((item) => {
                            return {
                                PeriodText: this.getNotFilledPeriodText(item),
                            };
                        }),
                        CheckPoints: unitsMap[unit.Id].CheckPoints,
                    });

                    addedItems[key].AnswersCount += unitsMap[unit.Id].AnswersCount;
                }
            });

            if (item.ObjectType === ObjectTypes.BusinessUnit && unitsMap.hasOwnProperty(item.Id)) {
                /// user has local rights
                this.tableData.push({
                    ObjectType: item.ObjectType,
                    Id: item.Id,
                    Name: item.Name,
                    NameForSorting: nameForSorting,
                    AnswersCount: unitsMap[item.Id].AnswersCount,
                    NotFilledOutPeriods: unitsMap[item.Id].NotFilledOutPeriods.map((item) => {
                        return {
                            PeriodText: this.getNotFilledPeriodText(item),
                        };
                    }),
                    CheckPoints: unitsMap[item.Id].CheckPoints,
                    ChildItems: [],
                });
            }
        });
    }

    private getNotFilledPeriodText(period) {
        let result = this.fiFormatDateService.formatDate(period.StartDateTime, 'date-time') + ' - ';
        const endDate = this.checklistService.getEndDateText(this.checklist, period.EndDateTime).toLowerCase();
        if (this.fiFormatDateService.formatDate(period.StartDateTime, 'date') !== endDate) {
            result += endDate + ' ';
        }
        if (!(this.checklist.PublicationSetting.PublicationType === PublicationType.Continous)) {
            result += this.fiFormatDateService.formatDate(period.EndDateTime, 'time');
        }
        return result;
    }

    protected getStatusName(status) {
        return this.appTextService.get(ChecklistModel.StatusTextIds[status]);
    }

    getSelectedStatus() {
        return this.status;
    }

    isStatusSelected(status) {
        return this.getSelectedStatus() === status.toString();
    }

    isDeviationSelected() {
        return this.isStatusSelected(ChecklistModel.Status.Deviation);
    }

    isNewUnitsSelected() {
        return this.isStatusSelected(ChecklistModel.Status.NotFilledOut);
    }

    isCompletedSelected() {
        return this.isStatusSelected(ChecklistModel.Status.Ok);
    }

    isFailedSelected() {
        return this.isStatusSelected(ChecklistModel.Status.Failed);
    }

    isDeviationCheckpointsSelected() {
        return this.isStatusSelected(ChecklistModel.Status.DeviationCheckpoints);
    }

    isOkCheckpointsSelected() {
        return this.isStatusSelected(ChecklistModel.Status.OkCheckpoints);
    }

    getStatusHeaderText() {
        return ChecklistModel.StatusPointsPerUnitTextIds[this.getSelectedStatus()];
    }

    chartClick($event) {
        this.checklistReportHelperService.onChartClick($event, (index) => {
            this.selectUnit(this.UnitCheckPointDetails[index].UnitId);
        });
    }

    private selectUnit(unitId) {
        this.checklistReportHelperService.selectBusinessUnit(unitId);
    }

    getCheckpointsUnitsTooltip(status) {
        return 'checklist.checkpoints_units_tooltip_' + status;
    }

    tableCellClick(data) {
        if (data.ObjectType == ObjectTypes.BusinessUnit) {
            this.selectUnit(data.Id);
        }
    }

    // note: not used ?
    chartHover(points) {}
}
